// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
// Controls
import { DefaultButton, DirectionalHint, IconButton, useTheme, getRTL } from "@fluentui/react";
import { addIconProps, boardSplitRegularIconProps, folderProps } from "@ms/uno-controls/lib/local/icons/icons.styles";
import { DynamicIcon } from "@ms/uno-controls/lib/local/controls/presentational/dynamicIcon/DynamicIcon";
import { Callout } from "@ms/uno-controls/lib/local/controls/presentational/callout/Callout";
// Utilities
import React from "react";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import { css } from "@fluentui/utilities/lib/css";
import { Strings } from "../../utilities/CultureUtilities";
// Styles
import { getUnoLeftNavClassNames, getUnoLeftNavStyles } from "./UnoLeftNav.styles";
import { getUnoLeftNavOptionClassNames, getUnoLeftNavOptionStyles } from "./UnoLeftNavOption.styles";
import { getUnoLeftNavDefaultButtonStyles, getUnoLeftNavCalloutStyles, getUnoLeftNavCalloutClassNames } from "./UnoLeftNavNewPlanSection.styles";
export var UnoLeftNavNewPlanSplitMenuOptions;
(function(UnoLeftNavNewPlanSplitMenuOptions) {
    /** New plan option */ UnoLeftNavNewPlanSplitMenuOptions["Plan"] = "Plan";
    /** New portfolio option */ UnoLeftNavNewPlanSplitMenuOptions["Portfolio"] = "Portfolio";
})(UnoLeftNavNewPlanSplitMenuOptions || (UnoLeftNavNewPlanSplitMenuOptions = {}));
/**
 * Uno leftNav new plan section
 * @param props - component props
 */ export function UnoLeftNavNewPlanSection({ newPlanInfo, isLeftNavCollapsed, keyCodeMapper, calloutDetails }) {
    // Ref for callout target
    const newPortfolioCalloutTarget = React.useRef(null);
    const theme = useTheme();
    /**
     * Get the split menu props
     */ const getSplitMenuProps = ()=>{
        if (!newPlanInfo.isSplitButtonEnabled) {
            return undefined;
        }
        const splitMenuItems = [
            {
                key: Strings.UnoLeftNav_NewPlanMenuPlanText,
                text: Strings.UnoLeftNav_NewPlanMenuPlanText,
                iconProps: boardSplitRegularIconProps,
                onClick: ()=>{
                    newPlanInfo.onSelect("Plan");
                }
            },
            {
                key: Strings.UnoLeftNav_NewPlanMenuPortfolioText,
                text: Strings.UnoLeftNav_NewPlanMenuPortfolioText,
                ref: newPortfolioCalloutTarget,
                iconProps: folderProps,
                onClick: (event)=>{
                    event.preventDefault(); // Prevent the menu from closing
                    event.stopPropagation();
                    newPlanInfo.onSelect("Portfolio");
                }
            }
        ];
        return {
            items: splitMenuItems,
            shouldFocusOnMount: true,
            useTargetWidth: true,
            beakWidth: 0,
            gapSpace: 4,
            calloutProps: {
                preventDismissOnLostFocus: newPlanInfo.isNewPortfolioCalloutVisible
            }
        };
    };
    /**
     * Handles key down event on list item
     * @param e - keyboard key down event
     */ const onListItemKeyDown = (e)=>{
        const keyDowned = KeyboardUtilities.getKey(e);
        if (keyDowned === KeyCode.SPACE) {
            e.preventDefault();
        }
        if (KeyboardUtilities.keyCodeIsSelectionKey(keyDowned, keyCodeMapper)) {
            if (newPlanInfo.onSelect != null) {
                newPlanInfo.onSelect("Plan");
            }
        }
    };
    const name = Strings.UnoLeftNav_NewPlanText;
    const leftNavClassNames = getUnoLeftNavClassNames(getUnoLeftNavStyles(isLeftNavCollapsed, theme, getRTL()));
    const newPlanClassName = css("newPlanSection", leftNavClassNames.leftNavOptionsList);
    const leftNavOptionClassNames = getUnoLeftNavOptionClassNames(getUnoLeftNavOptionStyles({
        breadCrumbToDisplay: false,
        theme: theme
    }));
    const isSplitButtonEnabled = newPlanInfo.isSplitButtonEnabled;
    const newPortfolioCalloutClassNames = getUnoLeftNavCalloutClassNames(getUnoLeftNavCalloutStyles(theme));
    // Icon for new plan button
    const icon = {
        normalIcon: addIconProps
    };
    // Render component content
    return /*#__PURE__*/ React.createElement("div", {
        className: css(newPlanClassName),
        id: ElementIds.LeftNavNewPlanButton
    }, /*#__PURE__*/ React.createElement(DefaultButton, {
        "data-is-focusable": true,
        role: "button",
        onKeyDown: onListItemKeyDown,
        onClick: ()=>newPlanInfo.onSelect("Plan"),
        tabIndex: 0,
        styles: getUnoLeftNavDefaultButtonStyles(theme, isSplitButtonEnabled),
        split: isSplitButtonEnabled,
        menuProps: getSplitMenuProps()
    }, /*#__PURE__*/ React.createElement("div", {
        className: leftNavOptionClassNames.linkContents
    }, /*#__PURE__*/ React.createElement("div", {
        className: css(icon.normalIcon.className)
    }, /*#__PURE__*/ React.createElement(DynamicIcon, {
        focused: false,
        stateBasedIcons: icon,
        name: name,
        size: 20,
        iconSvgSize: 20
    })), /*#__PURE__*/ React.createElement("div", {
        className: css(leftNavOptionClassNames.text)
    }, /*#__PURE__*/ React.createElement("span", {
        className: "leftNavItem",
        title: name
    }, name)))), newPlanInfo.isNewPortfolioCalloutVisible && /*#__PURE__*/ React.createElement(Callout, {
        directionalHint: DirectionalHint.rightBottomEdge,
        isBeakVisible: true,
        gapSpace: 5,
        setInitialFocus: true,
        target: newPortfolioCalloutTarget,
        onDismiss: ()=>newPlanInfo.onDismissCallout(),
        preventDismissOnLostFocus: true
    }, /*#__PURE__*/ React.createElement("div", {
        className: css(newPortfolioCalloutClassNames.calloutContent)
    }, /*#__PURE__*/ React.createElement("div", {
        className: css(newPortfolioCalloutClassNames.calloutHeader)
    }, /*#__PURE__*/ React.createElement("div", {
        className: css(newPortfolioCalloutClassNames.calloutTitle)
    }, calloutDetails.calloutTitle), /*#__PURE__*/ React.createElement(IconButton, {
        iconProps: {
            iconName: "Cancel"
        },
        onClick: (event)=>{
            event.stopPropagation();
            newPlanInfo.onDismissCallout();
        },
        className: css(newPortfolioCalloutClassNames.calloutDismissButton)
    })), /*#__PURE__*/ React.createElement("div", {
        className: css(newPortfolioCalloutClassNames.calloutDescription)
    }, calloutDetails.calloutDescription), /*#__PURE__*/ React.createElement("a", {
        href: "https://www.microsoft.com/en-us/microsoft-365/planner/project-plan-3?activetab=pivot:overviewtab",
        target: "_blank",
        rel: "noopener noreferrer",
        className: css(newPortfolioCalloutClassNames.calloutLink)
    }, calloutDetails.calloutLinkText))));
}
